const asideButtonsInstances = new WeakMap();

const MOBILE_BREAKPOINT = 834;
/**
 * Handles popup behavior for the aside buttons block.
 *
 * @class
 */
class AsideButtonsPopup {

	activeIndex = 0;

	/**
	 * Create popup behavior for a single block instance.
	 *
	 * @param {Element} block Aside buttons block element.
	 */
	constructor( block ) {

		this.block = block;
		this.header = document.querySelector( '.p-header' );
		this.triggers = [ ...block.querySelectorAll( '.js-popup-trigger' ) ];
		this.overlay = block.querySelector( '.js-popup-overlay' );
		this.closeButton = block.querySelector( '.js-popup-close' );
		this.panelHeadings = [ ...block.querySelectorAll( '.b-aside-buttons__panel-heading' ) ];
		this.panels = [ ...block.querySelectorAll( '.b-aside-buttons__form-panel' ) ];

		if ( ! this.isValid() ) {

			return;

		}

		this.bindEvents();
		this.initializeState();

	}

	/**
	 * Check if the block contains all required popup elements.
	 *
	 * @return {boolean} Whether instance can be initialized.
	 */
	isValid() {

		return Boolean(
			this.overlay &&
			this.closeButton &&
			this.triggers.length > 0 &&
			this.panels.length > 0
		);

	}

	/**
	 * Check whether the drawer should behave like mobile popup.
	 *
	 * @return {boolean} Whether the current viewport is mobile.
	 */
	isMobileViewport() {

		return window.innerWidth <= MOBILE_BREAKPOINT;

	}

	/**
	 * Sync current header height to the block CSS custom property.
	 *
	 * @return {void}
	 */
	setHeaderHeight = () => {

		const headerHeight = this.header?.getBoundingClientRect().height ?? 0;

		this.block.style.setProperty( '--header-height', `${ Math.round( headerHeight ) }px` );

	};

	/**
	 * Set active form panel for the selected button.
	 *
	 * @param {number} index Selected panel index.
	 * @return {void}
	 */
	setActivePanel( index ) {

		this.activeIndex = index;

		this.panels.forEach( ( panel, panelIndex ) => {

			const isActive = panelIndex === index;

			panel.classList.toggle( 'is-active', isActive );
			panel.setAttribute( 'aria-hidden', isActive ? 'false' : 'true' );

		} );

		this.panelHeadings.forEach( ( heading, headingIndex ) => {

			const isActive = headingIndex === index;

			heading.classList.toggle( 'is-active', isActive );
			heading.setAttribute( 'aria-hidden', isActive ? 'false' : 'true' );

		} );

	}

	/**
	 * Open drawer.
	 *
	 * @param {number} index Selected panel index.
	 * @return {void}
	 */
	openDrawer( index ) {

		this.setHeaderHeight();
		this.setActivePanel( index );
		this.overlay.classList.add( 'is-active' );
		this.overlay.setAttribute( 'aria-hidden', 'false' );
		this.triggers.forEach( ( trigger, triggerIndex ) => {

			trigger.setAttribute( 'aria-expanded', triggerIndex === index ? 'true' : 'false' );

		} );

		if ( this.isMobileViewport() ) {

			document.body.classList.add( 'has-aside-buttons-drawer-open' );

		}

		this.closeButton.focus();

	}

	/**
	 * Close drawer.
	 *
	 * @return {void}
	 */
	closeDrawer = () => {

		this.overlay.classList.remove( 'is-active' );
		this.overlay.setAttribute( 'aria-hidden', 'true' );
		this.triggers.forEach( ( trigger ) => trigger.setAttribute( 'aria-expanded', 'false' ) );
		document.body.classList.remove( 'has-aside-buttons-drawer-open' );
		this.triggers[ this.activeIndex ]?.focus();

	};

	/**
	 * Attach listeners needed by the popup.
	 *
	 * @return {void}
	 */
	bindEvents() {

		this.triggers.forEach( ( trigger, index ) => {

			trigger.addEventListener( 'click', ( event ) => {

				event.preventDefault();

				this.openDrawer( index );

			} );

		} );

		this.closeButton.addEventListener( 'click', this.closeDrawer );

		this.overlay.addEventListener( 'click', ( event ) => {

			if ( this.isMobileViewport() && event.target === this.overlay ) {

				this.closeDrawer();

			}

		} );

		document.addEventListener( 'keydown', ( event ) => {

			if ( event.key === 'Escape' && this.overlay.classList.contains( 'is-active' ) ) {

				this.closeDrawer();

			}

		} );

		window.addEventListener( 'resize', this.setHeaderHeight );

		if ( typeof ResizeObserver !== 'undefined' && this.header ) {

			const headerResizeObserver = new ResizeObserver( this.setHeaderHeight );

			headerResizeObserver.observe( this.header );

		}

	}

	/**
	 * Set initial block state.
	 *
	 * @return {void}
	 */
	initializeState() {

		this.setActivePanel( 0 );
		this.setHeaderHeight();

	}

	/**
	 * Public instance descriptor used by external code if needed.
	 *
	 * @return {{closeDrawer: Function, openDrawer: Function}} Public methods.
	 */
	getPublicApi() {

		return {
			closeDrawer: this.closeDrawer,
			openDrawer: ( index ) => this.openDrawer( index ),
		};

	}

}

document.querySelectorAll( '.b-aside-buttons.js-popup' ).forEach( ( block ) => {

	if ( asideButtonsInstances.has( block ) ) {

		return;

	}

	const instance = new AsideButtonsPopup( block );

	if ( instance.isValid() ) {

		asideButtonsInstances.set( block, instance.getPublicApi() );

	}

} );